import {
    Portal,
    useFormDialog,
    useLoading,
    usePaginator,
    useSearch,
    useSearchFilters,
    useSearchPaginate,
    useTranslation
} from '@components';
import { JobForm } from '@components/admin/JobForm';
import { JobCard } from '@components/jobs/list/JobCard';
import { goToJob } from '@components/routes';
import type {
    IActivityType,
    IContractType,
    IDepartment,
    IJob,
    IJobHasDepartment,
    IJobStatus,
    IPropertyGroupType,
    IResourceStatusType, ISecurity,
    IUserIdentity,
    IWork,
    IWorker,
    IWorkShift,
    IWorkspace,
    ModuleManager,
} from '@models';
import { rawJobStatusTypes } from '@models';
import type { IMachineryType, IWorkerDocumentType, IWorkerType } from '@models/resources';
import * as React from 'react';

import './Jobs.scss';

type GetJobStatusT = (workId: number, jobId: number) => Promise<IJobStatus[]>;
type IJobRow = IJob & {
    currentJobHasContractorId?: number;
}

export interface IProps {
    user: any;
    activityTypes: IActivityType[];
    clearSelectedJob: Function;
    contractTypes: IContractType[];
    departments: IDepartment[];
    propertyGroupTypes: IPropertyGroupType[];
    getJobs: Function;
    getJobContractorsViewModel: Function;
    saveJob: Function;
    security: ISecurity;
    moduleManager: ModuleManager;
    jobs: IJob[];
    work: IWork;
    workers: IWorker[];
    workShifts: IWorkShift[];
    workspaces: IWorkspace[];
    resourceStatusTypes: IResourceStatusType[];
    workerDocumentTypes: IWorkerDocumentType[];
    workerTypes: IWorkerType[];
    machineryTypes: IMachineryType[];
    getJobStatus: GetJobStatusT;
}

function SelectJob({
    activityTypes, security, workspaces, jobs,
    workerDocumentTypes, machineryTypes, workerTypes, work, initialize, moduleManager
}: {
    activityTypes: IActivityType[],
    departments: IDepartment[],
    getJobContractorsViewModel: Function,
    security: ISecurity,
    workers: IWorker[],
    workspaces: IWorkspace[],
    workerDocumentTypes: IWorkerDocumentType[],
    workerTypes: IWorkerType[],
    machineryTypes: IMachineryType[],
    jobs: IJobRow[],
    selectJob: Function,
    work: IWork,
    initialize: Function,
    getJobStatus: Function;
    user?: IUserIdentity;
    moduleManager: ModuleManager;
}) {
    return <div className='SelectJob r h-center'>
        {(jobs ?? []).map((j) =>
            <JobCard
                key={Math.random() + j.id}
                job={j}
                security={security}
                workspaces={workspaces}
                activityTypes={activityTypes}
                workerDocumentTypes={workerDocumentTypes}
                workerTypes={workerTypes}
                machineryTypes={machineryTypes}
                work={work}
                reload={initialize}
                currentJobHasContractorId={j.currentJobHasContractorId}
                moduleManager={moduleManager} />
        )}
    </div>
}

enum JobListMode {
    MYJOBS = 0,
    DEPARTMENT = 1,
    ALLJOBS = 2,
    INVOLVED = 3,
    SUBCONTRACTED = 4,
}

let __jobListMode = JobListMode.ALLJOBS;

export function Jobs(props: IProps) {
    const { t } = useTranslation();
    const loading = useLoading(false);
    const [jobListMode, setJobListMode] = React.useState<JobListMode>(__jobListMode);
    const [canCreateJobState, setCanCreateJobState] = React.useState<boolean>(false);

    const isOblique = React.useRef(false);

    const isNotOblique = React.useRef(false);

    const workerIds = React.useMemo(() => props.security.getWorkerIds(), [props.security]);

    const departments = props.departments;

    const initialize = React.useCallback(async () => {
        props.clearSelectedJob();
    }, [props.clearSelectedJob]);


    React.useEffect(() => {
        initialize();
    }, [initialize]);


    const [responsibles, setResponsibles] = React.useState<{ id: number, name: string }[]>([]);
    const [dataValue, setDataValue] = React.useState<IJob[]>([]);
    const [workSpaces, setWorkSpaces] = React.useState<{ id: number, name: string }[]>();

    const [contractorParents, setContractorParents] = React.useState<{ id: number, name: string }[]>([]);

    React.useEffect(() => {
        __jobListMode = jobListMode;
    }, [jobListMode]);

    const formDialog = useFormDialog({
        editTitle: t('Edit job'),
        addTitle: t('Add job'),
    });


    const calculateIsOblique = React.useCallback(() => {
        for (let i = 0; i < workerIds.length; i++) {
            const w = workerIds[i];

            for (let j = 0; j < departments.length; j++) {
                const d = departments[j];

                if (d.workers?.some(wd => w === wd.id) && d.isOblique) {
                    isOblique.current = true;
                }

                if (d.workers?.some(wd => w === wd.id) && !d.isOblique) {
                    isNotOblique.current = true;
                }
            }
        }
    }, [workerIds, departments]);

    const getSearch = React.useCallback(() => {
        calculateIsOblique();
        var res: string;
        if (props.security.isContractor() && ![23,22,28].includes(props.work.id)) {
            res = 'jobList/contractors';
        }
        else if ([23,22,28].includes(props.work.id) && jobListMode === JobListMode.ALLJOBS && props.security.isContractor()) {
            res = 'jobList/contractors.all';
        }
        else if (props.security.isGestor()) {
            res = 'jobList/gestor';
        }
        else if (jobListMode === JobListMode.ALLJOBS && props.security.hasPolicy('jobs.list') && ![23,22,28].includes(props.work.id)) {
            res = 'jobList/gestor';
        }
        else if (jobListMode === JobListMode.ALLJOBS && !props.security.hasPolicy('jobs.list') && ![23,22,28].includes(props.work.id)) {
            res = 'jobList/workers.all';
        }
        else if ([23,22].includes(props.work.id) && jobListMode === JobListMode.ALLJOBS && isOblique.current && props.security.isWorker()) {
            res = 'jobList/gestor';
        }
        else if ([23,22].includes(props.work.id) && jobListMode === JobListMode.ALLJOBS && isNotOblique.current && props.security.isWorker()) {
            res = 'jobList/workers.oblique.all';
        }
        else if (props.work.id === 28 && jobListMode === JobListMode.ALLJOBS && props.security.isWorker()) {
            res = 'jobList/workers.all';
        }
        else if (jobListMode === JobListMode.MYJOBS && [23,22,28].includes(props.work.id) && props.security.isContractor()) {
            res = 'jobList/contractors.own';
        }
        else if (jobListMode === JobListMode.MYJOBS && ![23,22,28].includes(props.work.id)) {
            res = 'jobList/workers.own';
        }
        else if (jobListMode === JobListMode.MYJOBS && [23,22].includes(props.work.id) && props.security.isWorker()) {
            res = 'jobList/workers.own.azsa';
        }
        else if (props.work.id === 28 && jobListMode === JobListMode.MYJOBS && props.security.isWorker()) {
            res = 'jobList/workers.own';
        }
        else if (jobListMode === JobListMode.DEPARTMENT && ![23,22,28].includes(props.work.id) && !isOblique.current) {
            res = 'jobList/departments.all';
        }
        else if (jobListMode === JobListMode.DEPARTMENT && [23,22].includes(props.work.id)) {
            res = 'jobList/departments.azsa.all';
        }
        else if (props.work.id === 28 && jobListMode === JobListMode.DEPARTMENT) {
            res = 'jobList/departments.all';
        }
        else if ([23,22].includes(props.work.id) && jobListMode === JobListMode.INVOLVED) {
            res = 'jobList/workers.involved';
        }
        else if ([23,22,28].includes(props.work.id) && jobListMode === JobListMode.SUBCONTRACTED) {
            res = 'jobList/contractors.subcontracted';
        }
        else {
            res = '';
        }
        return res;
    }, [jobListMode, props.work.id, props.security, calculateIsOblique]);

    const filterStatusTypes = React.useMemo(() =>
        props.moduleManager.filterDependency('job.statusTypes', {
            security: props.security,
            work: props.work,
        }),
        [props.moduleManager, props.security, props.work]
    );
    const calculateStatusTypes = React.useCallback((jobStatusTypes: {id: number, name : string}[]) => {
        return jobStatusTypes.filter(filterStatusTypes);
    }, [filterStatusTypes]);
    const statusTypesRef = React.useMemo(() => calculateStatusTypes(rawJobStatusTypes)
        .map(s => {
            const formatted = props.moduleManager.format('job.statusTypes', s, { security: props.security, work: props.work });
            return typeof formatted === 'string' ? { ...s, name: formatted } : s;
        }), [calculateStatusTypes, props.moduleManager, props.security, props.work]);
    const contractorParentsRef = React.useMemo(() => Array.from(new Set(contractorParents)), [contractorParents]);

    const [contractors, setContractors] = React.useState<any[]>([]);

    const userFiltersReferences = React.useMemo(() => {
        const references: any = {
            current_status_type: statusTypesRef,
            responsible: responsibles,
            parent_contractor: contractorParentsRef,
            select_contractor: contractors,
        };

        if (workSpaces && workSpaces.length > 0) {
            references.workspace = workSpaces;
        }

        return references;
    }, [workSpaces, statusTypesRef, responsibles, contractorParentsRef, contractors]);

    const userFilters = useSearchFilters({
        security: props.security,
        workId: props.work.id,
        name: getSearch(),
        references: userFiltersReferences,
        persist: true,
        awaitForDoSearch: true
    });

    const setFilterRef = React.useRef(userFilters.setFilter);
    setFilterRef.current = userFilters.setFilter;

    const mergeFilters = React.useCallback((filters: any) => {
        if (filters.current_status_type && filters.current_status_type !== 0) {
            return filters;
        }
        else {
            if (filters.current_status_type === 0) {
                setFilterRef.current('current_status_type', null);
            }
            return { ...filters, exclude_status: 4 };
        }
    }, []);

    const parentsSearch = useSearch<any>({
        workId: props.work.id,
        search: 'jobList/parents',
    });

    const worksSpacesSearch = useSearch<any>({
        workId: props.work.id,
        search: 'jobList/workspaces',
        filters: {
            workerId: props.work.id,
        },
    });

    const calculateUserWorkSpaces = React.useCallback(() => {
        let result : { id: number, name: string }[] = [];
        if (props.security.isWorker()) {
             worksSpacesSearch.value?.forEach((w: any) => {
                result.push({ id: w.id, name: w.name })
            })

        } else {
            props.workspaces?.forEach((w: any) => {
                result.push({ id: w.id, name: w.name })
            })
        }
        if (result.length > 0) {
            setWorkSpaces(result);
        }
    }, [worksSpacesSearch.value, props.security, props.workspaces])

    React.useEffect(() => {
        calculateUserWorkSpaces();
    }, [calculateUserWorkSpaces]);

    console.log(props.security);

    const responsiblesSearch = useSearch<any>({
        workId: props.work.id,
        search: 'jobList/oblique.responsibles',
        filters: {
            workId: props.work.id,
            userDepartment: props.security.user?.obliqueDepartments?.length === 0 ? props.security.user?.departments : null,
        },
    });
    const contractorsSearch = useSearch<any>({
        workId: props.work.id,
        search: 'jobList/contractorsOptions',
        filters: {
            workId: props.work.id,
        },
    });

    React.useEffect(() => {
        const options = contractorsSearch.value.map((c: any) => ({ id: c.id, name: c.name.trim() }));
        setContractors(options.sort((a, b) => a.name.localeCompare(b.name)));
    }, [contractorsSearch.value]);

    const prevFiltersRef = React.useRef<any>(null);
    const stableUserFilters = React.useMemo(() => {
        const currentFilters = userFilters.filters;
        const prevFilters = prevFiltersRef.current;

        if (prevFilters) {
            const keys = Object.keys(currentFilters);
            const prevKeys = Object.keys(prevFilters);

            if (keys.length === prevKeys.length &&
                keys.every(key => currentFilters[key] === prevFilters[key])) {
                return prevFilters;
            }
        }

        prevFiltersRef.current = currentFilters;
        return currentFilters;
    }, [userFilters.filters]);

    const initSearchesRef = React.useRef(() => {});
    initSearchesRef.current = () => {
        props.security.isContractor() && parentsSearch.doSearch() && responsiblesSearch.doSearch();
        props.security.isWorker() && worksSpacesSearch.doSearch();
        contractorsSearch.doSearch();
    };

    React.useEffect(() => {
        initSearchesRef.current();
    }, []);

    React.useEffect(() => {
        const filterResponsible = props.moduleManager.filterDependency('responsible', {
            security: props.security,
            work: props.work,
        });
        setResponsibles(responsiblesSearch.value.filter(filterResponsible))
    }, [props.moduleManager, props.security, props.work, responsiblesSearch.value]);

    const dataFilters = React.useMemo(() => {
        const { workspace, ...restFilters } = mergeFilters(stableUserFilters);
        const filters: any = {
            workId: props.work.id,
            responsibleId: props.security.getWorkerIds()[0],
            ...restFilters,
        };

        if (workspace && workspace.length > 0 && workspace[0] !== 0) {
            filters.workspace = workspace;
        }

        if (workSpaces && workSpaces.length > 0) {
            filters.userWorkSpaces = workSpaces.map(w => w.id);
        }

        const scopedDepartments = props.moduleManager.componentProperties<number[]>('scopedDepartments', []);
        const user = props.security.user;
        if (scopedDepartments !== undefined && scopedDepartments?.length > 0 && user?.departments?.some(d => scopedDepartments.includes(d))) {
            filters.department_id = user?.departments?.filter(
                d => !user?.roleMarkerDepartments?.includes(d));
        }

        if (jobListMode === JobListMode.DEPARTMENT) {
            const depts = user?.departments;
            if (depts && depts.length > 0) {
                filters.department_id = depts;
            }
            delete filters.responsibleId;
        }

        return filters;
    }, [props.work.id, props.security.getWorkerIds, props.security.user, workSpaces, props.security.user?.departments, props.security.user?.roleMarkerDepartments, stableUserFilters, mergeFilters, props.moduleManager, jobListMode]);

    const dataDependencies = React.useMemo(() => [dataFilters, stableUserFilters, jobListMode, workSpaces], [dataFilters, stableUserFilters, jobListMode, workSpaces]);

    const searchValue = React.useMemo(() => getSearch(), [getSearch]);

    const data = useSearchPaginate<IJobRow>({
        workId: props.work.id,
        normalizeKeys: true,
        filters: dataFilters,
        search: searchValue,
        dependencies: dataDependencies,
        limit: 8,
        lazy: true,
    });

    const calculateParentContractors = React.useCallback(() => {
      const parents = parentsSearch.value;
      if (props.work.id === 23) {
        parents &&
          parents[0]?.id !== 0 &&
          parents.unshift({ id: 0, name: "AZSA" });
      }
      setContractorParents(parentsSearch.value);
    }, [parentsSearch.value, props.work.id]);

    React.useEffect(() => {
        setDataValue(data.data);
    }, [data.data]);

    React.useEffect(() => {
        if (data.data && data.data.length > 0) {
            calculateParentContractors();
        }
    }, [calculateParentContractors, data.data]);

    const filterByParentContractor = React.useCallback((jobs: any) => {
        if (!jobs) return jobs;
        const result = jobs.filter((j: any) => j.parentContractorId === (userFilters.filters.parent_contractor === 0 ? null : userFilters.filters.parent_contractor));
        return result;
    }, [userFilters.filters.parent_contractor]);

    const displayData = React.useMemo(() => {
        if (userFilters.filters.parent_contractor !== undefined && userFilters.filters.parent_contractor !== null) {
            return filterByParentContractor(dataValue);
        }
        return dataValue;
    }, [dataValue, userFilters.filters.parent_contractor, filterByParentContractor]);

    const paginator = usePaginator<IJob>({
        className: 'transparent lg pd-right',
        data: data.value,
        rowNumbers: [8, 16, 32, 64],
        onChange: React.useCallback((page, limit) => data.doSearch(dataFilters, page, limit), [dataFilters, data.doSearch]),
    });

    const reload = () => {
        data.doSearch();
    }

    const saveJob = loading.wrap(async (w: IJob, departments: IJobHasDepartment[]) => {
        await props.saveJob(w, departments);
        await data.doSearch();

        formDialog.clear();
    });

    const calculateOptions = () => {
        const options: React.ReactElement[] = [];
        if ((props.work.id === 23 || props.work.id === 28) && props.security.isWorker()) {
            options.push(<React.Fragment key='worker'>
                <option key='all' className={'font'} value={JobListMode.ALLJOBS}>{t('jobs.list.allJobs.pessma')}</option>
                <option key='my' className={'font'} value={JobListMode.MYJOBS}>{t('jobs.list.myjobs.pessma')}</option>
                <option key='inv' className={'font'} value={JobListMode.INVOLVED}>{t('jobs.list.involved')}</option>
                <option key='dept' className={'font'} value={JobListMode.DEPARTMENT}>{t('jobs.list.departments.pessma')}</option>
            </React.Fragment>)
        } else if ((props.work.id === 23 || props.work.id === 28) && props.security.isContractor()) {
            options.push(<React.Fragment key='contractor'>
                <option key='all' className={'font'} value={JobListMode.ALLJOBS}>{t('jobs.list.allJobs.pessma')}</option>
                <option key='my' className={'font'} value={JobListMode.MYJOBS}>{t('jobs.list.myjobs.pessma')}</option>
                <option key='sub' className={'font'} value={JobListMode.SUBCONTRACTED}>{t('jobs.list.subcontracted')}</option>
            </React.Fragment>)
        }
        else {
            if (!isOblique.current) {
                options.push(<React.Fragment key='not-oblique'><option key='all' className={'font'} value={JobListMode.ALLJOBS}>{t('jobs.list.allJobs')}</option>
                    <option key='my' className={'font'} value={JobListMode.MYJOBS}>{t('jobs.list.myjobs')}</option>
                    <option key='dept' className={'font'} value={JobListMode.DEPARTMENT}>{t('jobs.list.departments')}</option></React.Fragment>)
            } else {
                options.push(<React.Fragment key='oblique'><option key='all' className={'font'} value={JobListMode.ALLJOBS}>{t('jobs.list.allJobs.pessma')}</option>
                    <option key='my' className={'font'} value={JobListMode.MYJOBS}>{t('jobs.list.myjobs.pessma')}</option></React.Fragment>)
            }
        }
        return <select className='transparent' value={jobListMode} onChange={e => setJobListMode(parseInt(e.target.value, 10))}>
            {options}
        </select>
    }

    const calculateCreateJob = React.useCallback(async () => {
        const ctx = {
            security: props.security,
            work: props.work,
        }
        const res = await props.moduleManager.requirePermission('jobs.create', ctx, props.security.hasPermission('jobs.create'));
        setCanCreateJobState(res ?? false);
    }, [props.moduleManager, props.security, props.work]);

    React.useEffect(() => {
        calculateCreateJob();
    }, [calculateCreateJob]);

    const selectJob = (jobId: number) => goToJob(props.work.id, jobId);
    return <div className='c'>
        {paginator(<userFilters.Component />)}

        <Portal container='#breadcrumb-right'>
            <div className='r'>
                {(props.security.isWorker() || (props.security.isContractor() && [23, 28].includes(props.work.id))) &&
                    calculateOptions()}
                {canCreateJobState &&
                    <button
                        type='button'
                        onClick={() => formDialog.showAdd()}>
                          <i className='pi pi-plus sm pd-right' />
                          {t((props.work.id === 23 && props.security.isWorker()) ? 'Create job pessma' : 'Create job')}
                    </button>
                }
            </div>
        </Portal>

        {formDialog.render((data: IJob) =>
            <JobForm
                moduleManager={props.moduleManager}
                activityTypes={props.activityTypes}
                loading={loading}
                contractTypes={props.contractTypes}
                data={data}
                work={props.work}
                workId={props.work.id}
                notifications={[]}
                onSubmit={saveJob}
                workShifts={props.workShifts}
                workspaces={props.workspaces}
                departments={props.departments}
                security={props.security}
                propertyGroupTypes={props.propertyGroupTypes}
                footer={valid => <formDialog.Footer disabled={!valid || loading.isLoading()} />}
          />,
          [23, 22, 28].includes(props.work.id))}
        {data.renderLoading({
            text: t('jobs.list.loading')
        })}
        {<SelectJob
            activityTypes={props.activityTypes}
            departments={props.departments}
            jobs={displayData}
            work={props.work}
            getJobContractorsViewModel={props.getJobContractorsViewModel}
            security={props.security}
            workers={props.workers}
            workspaces={props.workspaces}
            selectJob={selectJob}
            workerDocumentTypes={props.workerDocumentTypes}
            workerTypes={props.workerTypes}
            machineryTypes={props.machineryTypes}
            initialize={reload}
            getJobStatus={props.getJobStatus}
            moduleManager={props.moduleManager} />}
    </div>
}
